import confetti from 'canvas-confetti';
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Rocket, Check, Sparkles } from 'lucide-react';

interface WelcomeModalProps {
  prenom: string;
  onStartGuide: () => void;
  onExplore: () => void;
}

// Composant pour styliser le logo B8ild avec le 8 en jaune
function B8ildLogo({ className = "" }: { className?: string }) {
  return (
    <span className={className}>
      B<span className="text-[#F59E0B]">8</span>ild
    </span>
  );
}

export function WelcomeModal({ prenom, onStartGuide, onExplore }: WelcomeModalProps) {
  const [showSkip, setShowSkip] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowSkip(true), 4000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Confettis subtils à l'ouverture
    const duration = 2500;
    const end = Date.now() + duration;

    const frame = () => {
      // Confettis depuis la gauche
      confetti({
        particleCount: 2,
        angle: 60,
        spread: 45,
        origin: { x: 0, y: 0.6 },
        colors: ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'],
        gravity: 0.8,
      });
      // Confettis depuis la droite
      confetti({
        particleCount: 2,
        angle: 120,
        spread: 45,
        origin: { x: 1, y: 0.6 },
        colors: ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'],
        gravity: 0.8,
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();

    // Burst initial plus visible
    confetti({
      particleCount: 80,
      spread: 60,
      origin: { y: 0.5 },
      colors: ['#3b82f6', '#10b981', '#f59e0b'],
    });
  }, []);

  const benefits = [
    'Voir ta rentabilité en temps réel sur chaque chantier',
    'Gérer tes équipes, les rotations, leur planning et leurs coûts',
    'Scanner tes devis et factures en 1 clic',
    "Anticiper les problèmes avant qu'ils n'arrivent",
  ];

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-lg border-primary/20 bg-background/95 backdrop-blur-sm"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Bienvenue sur B8ild</DialogTitle>
        </DialogHeader>

        {/* Bouton fermer masqué - on force le choix */}

        {/* Header avec emoji et titre */}
        <div className="text-center pt-2">
          <div className="flex justify-center mb-3">
            <div className="relative">
              <span className="text-5xl animate-bounce inline-block">🎉</span>
              <div className="absolute -top-1 -right-1 h-3 w-3 bg-primary rounded-full animate-ping" />
            </div>
          </div>

          <h2 className="text-2xl font-bold mb-1">
            Bienvenue sur <B8ildLogo />{prenom ? `, ${prenom}` : ''} !
          </h2>

          <p className="text-[#F59E0B] text-sm font-semibold">
            Ton essai gratuit de 14 jours commence maintenant.
          </p>
        </div>

        {/* Séparateur */}
        <div className="border-t border-border/50 my-3" />

        {/* Bénéfices */}
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground mb-3">
            À partir d'aujourd'hui, tu vas pouvoir :
          </p>
          <ul className="space-y-2.5">
            {benefits.map((benefit, index) => (
              <li key={index} className="flex items-start gap-2.5">
                <div className="mt-0.5 h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Check className="h-3 w-3 text-primary" />
                </div>
                <span className="text-sm">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Séparateur */}
        <div className="border-t border-border/50 my-3" />

        {/* Call to action - Guide (mise en avant) */}
        <div className="bg-[#F59E0B]/10 rounded-xl p-4 border-2 border-[#F59E0B]/40 shadow-[0_0_15px_rgba(245,158,11,0.15)]">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl bg-[#F59E0B]/20 flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-6 w-6 text-[#F59E0B]" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-base text-white">
                Pour maîtriser <B8ildLogo /> en 10 minutes
              </p>
              <p className="text-sm text-white/70 mt-1">
                Suis notre <span className="font-medium text-[#F59E0B]">checklist</span> : <span className="text-white font-medium">10 étapes</span> simples pour devenir autonome.
              </p>
            </div>
          </div>
        </div>

        {/* Boutons */}
        <div className="flex flex-col gap-2 pt-2">
          <Button
            onClick={onStartGuide}
            size="lg"
            className="w-full"
          >
            <Rocket className="mr-2 h-4 w-4" />
            Commencer le guide
          </Button>

          <button
            onClick={onExplore}
            className={`text-sm text-muted-foreground hover:text-foreground transition-all duration-500 py-2 ${showSkip ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          >
            Explorer par moi-même
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
